import {
  type Descendant,
  Editor,
  Element,
  type MaximizeMode,
  Node,
  type NodeEntry,
  Path,
  Range,
  Text,
  Transforms,
} from 'slate';
import {
  type Marks,
  type TableCellElement,
  type TableElement,
  type TableRowElement,
} from '../../types/aha-editor';
import {
  isTableElement,
  isTableCellElement,
  isParagraphElement,
  isTableRowElement,
} from '../../types/guards';
import type TableStructure from './TableStructure';
import {
  type CellCoordinates,
  type CellInfo,
  getTableStructureFromElement,
  getTableStructureFromSelection,
} from './TableStructure';
import PathUtils from '../../AhaEditorUtils/PathUtils';
import TableRange from './TableRange';
import type TableSelection from './TableSelection';
import emptyParagraph from '../../AhaEditorUtils/util/emptyParagraph';

// Utilities for working with tables.
class TableUtils {
  static currentTable(editor: Editor) {
    return Editor.above(editor, {
      match: isTableElement,
      mode: 'lowest',
    });
  }

  // Get the table element at the current selection.
  static currentTableElement(editor: Editor) {
    const tableEntry = TableUtils.currentTable(editor);
    if (!tableEntry) return;

    return tableEntry[0];
  }

  // Get the path of the table at the current selection.
  static currentTablePath(editor: Editor) {
    const tableEntry = TableUtils.currentTable(editor);
    if (!tableEntry) return;
    return tableEntry[1];
  }

  static currentCell(editor: Editor) {
    return Editor.above(editor, {
      match: isTableCellElement,
      mode: 'lowest',
    });
  }

  static currentCellPath(editor: Editor) {
    const cellEntry = TableUtils.currentCell(editor);
    if (!cellEntry) return;
    return cellEntry[1];
  }

  static parentCell(editor: Editor, path: Path) {
    return Editor.above(editor, {
      at: path,
      match: isTableCellElement,
      mode: 'lowest',
    });
  }

  static parentTable(
    editor: Editor,
    path: Path,
    mode: MaximizeMode | undefined = 'lowest'
  ) {
    return Editor.above(editor, {
      at: path,
      match: isTableElement,
      mode,
    });
  }

  /**
   * A useful wrapper to interact with the current table. Returns false if
   * we could not find a table element, otherwise returns true.
   */
  static withCurrentTableStructure(
    editor: Editor,
    fn: (
      currentTable: NodeEntry<TableElement>,
      tableStructure: TableStructure
    ) => void
  ): boolean {
    const currentTable = TableUtils.currentTable(editor);
    if (!currentTable) return false;

    const tableStructure = getTableStructureFromElement(
      editor,
      currentTable[0]
    );

    fn(currentTable, tableStructure);

    return true;
  }

  static tableCellInTable(editor: Editor, tablePath: Path, targetPath: Path) {
    return Editor.above<TableCellElement>(editor, {
      at: targetPath,
      match: (n, p) =>
        isTableCellElement(n) &&
        Path.isAncestor(tablePath, p) &&
        tablePath.length + 2 === p.length,
    });
  }

  static tableAncestor(editor: Editor, range: Range) {
    return Editor.above(editor, {
      match: isTableElement,
      mode: 'highest',
      at: range,
    });
  }

  static removeColumnWidth(
    editor: Editor,
    tablePath: Path,
    columnIndex: number
  ) {
    const table = Editor.node(editor, tablePath);
    if (!table || !isTableElement(table[0])) return;

    const columnWidths: number[] = table[0].table_columnWidths
      ? [...table[0].table_columnWidths]
      : [];
    if (!columnWidths.length) return;

    columnWidths.splice(columnIndex, 1);

    Transforms.setNodes(
      editor,
      { table_columnWidths: columnWidths },
      { at: tablePath }
    );

    TableUtils.redistributeColumnWidths(editor, tablePath);
  }

  static insertColumnWidth(
    editor: Editor,
    tablePath: Path,
    columnIndex: number
  ) {
    const table = Editor.node(editor, tablePath);
    if (!table || !isTableElement(table[0])) return;

    const columnWidths: number[] = table[0].table_columnWidths
      ? [...table[0].table_columnWidths]
      : [];
    if (!columnWidths.length) return;

    const prevWidth = columnWidths[columnIndex];

    columnWidths.splice(columnIndex + 1, 0, prevWidth);

    Transforms.setNodes(
      editor,
      { table_columnWidths: columnWidths },
      { at: tablePath }
    );

    TableUtils.redistributeColumnWidths(editor, tablePath);
  }

  static redistributeColumnWidths(editor: Editor, tablePath: Path) {
    const table = Editor.node(editor, tablePath);
    if (!table || !isTableElement(table[0])) return;

    const columnWidths = table[0].table_columnWidths;
    if (!columnWidths) return;

    const originalTableWidth = 100;
    const newTableWidth = columnWidths.reduce((sum, n) => sum + n, 0);
    const ratio = originalTableWidth / newTableWidth;
    const newColumnWidths = columnWidths.map((width) => width * ratio);

    Transforms.setNodes(
      editor,
      { table_columnWidths: newColumnWidths },
      { at: table[1] }
    );
  }

  static insertColumn(editor: Editor, path: Path, before = false) {
    if (!editor) return;

    const table = TableUtils.parentTable(editor, path);
    if (!table) return;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const coords = tableStructure.pathToCoordinates(path.slice(-2));
    if (!coords) return;

    const originalCell = tableStructure.cell(
      coords.columnIndex,
      coords.rowIndex
    );
    if (!originalCell) return;

    // If inserting before, this is the current column index.
    // If inserting after, we need to find the furthest column index that the cell covers,
    // so we add any colspan to the index here
    const originColIndex = before
      ? coords.columnIndex
      : coords.columnIndex + ((originalCell.tableCell.table_colspan ?? 1) - 1);

    const cellsInColumn = tableStructure.actualCellsInColumn(originColIndex);

    // We need to do this without normalizing otherwise the table normalizations
    // will try to replace missing cells to make the table rectangular
    Editor.withoutNormalizing(editor, () => {
      for (const { rowIndex, columnIndex } of cellsInColumn) {
        const cell = tableStructure.cell(columnIndex, rowIndex);
        if (!cell) continue;

        const startColumn = cell.relativePath[1];
        const colSpan: number = cell.tableCell.table_colspan ?? 1;
        const rowSpan: number = cell.tableCell.table_rowspan ?? 1;
        const modifier = before ? 0 : 1;
        const withinColspan =
          originColIndex + modifier > startColumn &&
          originColIndex + modifier < startColumn + colSpan;

        if (withinColspan) {
          Transforms.setNodes(
            editor,
            { table_colspan: colSpan + 1 },
            { at: [...table[1], ...cell.relativePath] }
          );
        } else {
          const newCell: TableCellElement = {
            type: 'table-cell',
            children: [emptyParagraph()],
          };
          if (rowSpan > 1) {
            newCell.table_rowspan = rowSpan;
          }
          Transforms.insertNodes(editor, newCell, {
            at: [
              ...table[1],
              cell.relativePath[0],
              cell.relativePath[1] + modifier,
            ],
          });
        }
      }

      // Update column widths.
      TableUtils.insertColumnWidth(editor, table[1], originColIndex);
    });
  }

  static insertRow(editor: Editor, path: Path, before = false) {
    if (!editor?.selection) return;

    const table = TableUtils.parentTable(editor, path);
    if (!table) return;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const coords = tableStructure.pathToCoordinates(path.slice(-2));
    if (!coords) return;

    const originalCell = tableStructure.cell(
      coords.columnIndex,
      coords.rowIndex
    );
    if (!originalCell) return;

    // If inserting before, this is the current row index.
    // If inserting after, we need the furthest row index that the cell covers,
    // so we add any rowspan to the index here
    const originRowIndex = before
      ? coords.rowIndex
      : coords.rowIndex + ((originalCell.tableCell.table_rowspan ?? 1) - 1);

    const modifier = before ? 0 : 1;
    const rowPath = [...table[1], originRowIndex + modifier];

    const cellsInRow = tableStructure.actualCellsInRow(originRowIndex);

    const columnCells: TableCellElement[] = [];

    Editor.withoutNormalizing(editor, () => {
      for (const { rowIndex, columnIndex } of cellsInRow) {
        const cell = tableStructure.cell(columnIndex, rowIndex);
        if (!cell) continue;

        const startRow = cell.relativePath[0];
        const colSpan: number = cell.tableCell.table_colspan ?? 1;
        const rowSpan: number = cell.tableCell.table_rowspan ?? 1;
        const withinRowspan =
          originRowIndex + modifier > startRow &&
          originRowIndex + modifier < startRow + rowSpan;

        if (withinRowspan) {
          Transforms.setNodes(
            editor,
            { table_rowspan: rowSpan + 1 },
            { at: [...table[1], ...cell.relativePath] }
          );
        } else {
          const newCell: TableCellElement = {
            type: 'table-cell',
            children: [emptyParagraph()],
          };
          if (colSpan > 1) {
            newCell.table_colspan = colSpan;
          }
          columnCells.push(newCell);
        }
      }

      const newRow: TableRowElement = {
        type: 'table-row',
        children: [...columnCells],
      };

      Transforms.insertNodes(editor, newRow, {
        at: rowPath,
      });
    });
  }

  static deleteRow(editor: Editor, rowIndex: number) {
    const table = TableUtils.currentTable(editor);
    if (!table) return;

    const tableStructure = getTableStructureFromElement(editor, table[0]);

    let minRowSpan = Number.MAX_SAFE_INTEGER;
    for (let x = tableStructure.width - 1; x >= 0; x--) {
      const cell = tableStructure.cell(x, rowIndex);
      if (!cell) continue;
      minRowSpan = Math.min(minRowSpan, cell.tableCell.table_rowspan ?? 1);
    }

    const rowSpanAdjustment = minRowSpan - 1;

    if (rowSpanAdjustment > 0) {
      Editor.withoutNormalizing(editor, () => {
        // If all cells in a row are covered by a rowspan, we can remove
        // the excess and the empty rows that go with it.
        // This makes it much easier to work out what to delete.
        const normalized = new Set<string>();
        for (let x = tableStructure.width - 1; x >= 0; x--) {
          const cell = tableStructure.cell(x, rowIndex);
          if (!cell) continue;
          const pathKey = cell.relativePath.toString();
          if (normalized.has(pathKey)) continue;

          normalized.add(pathKey);

          const currentRowSpan = cell.tableCell.table_rowspan ?? 1;

          if (currentRowSpan - rowSpanAdjustment === 1) {
            Transforms.unsetNodes(editor, 'table_rowspan', {
              at: [...table[1], ...cell.relativePath],
            });
          } else {
            Transforms.setNodes(
              editor,
              { table_rowspan: currentRowSpan - rowSpanAdjustment },
              {
                at: [...table[1], ...cell.relativePath],
              }
            );
          }
        }

        for (let i = rowSpanAdjustment; i > 0; i--) {
          Transforms.removeNodes(editor, {
            at: [...table[1], rowIndex + i],
          });
        }
      });
    }

    Editor.withoutNormalizing(editor, () => {
      // We need to reload the table and tableStructure for this block
      const table = TableUtils.currentTable(editor);
      if (!table) return;

      const tableStructure = getTableStructureFromElement(editor, table[0]);

      for (let x = tableStructure.width - 1; x >= 0; x--) {
        const cell = tableStructure.cell(x, rowIndex);
        if (!cell) continue;

        const rowSpan = cell.tableCell.table_rowspan ?? 1;
        const coordsOfActual = tableStructure.pathToCoordinates(
          cell.relativePath
        );
        if (!coordsOfActual) continue;

        const atStartOfColspan = coordsOfActual.columnIndex === x;
        const atStartOfRowspan = coordsOfActual.rowIndex === rowIndex;

        if (rowSpan > 1) {
          if (atStartOfColspan) {
            // Decrease rowspan by 1
            if (rowSpan - 1 === 1) {
              Transforms.unsetNodes(editor, 'table_rowspan', {
                at: [...table[1], ...cell.relativePath],
              });
            } else {
              Transforms.setNodes(
                editor,
                { table_rowspan: rowSpan - 1 },
                {
                  at: [...table[1], ...cell.relativePath],
                }
              );
            }

            if (atStartOfRowspan) {
              // Move cell into next row
              Transforms.moveNodes(editor, {
                at: [...table[1], ...cell.relativePath],
                to: [
                  ...table[1],
                  rowIndex + 1,
                  tableStructure.actualColumnIndex(x, rowIndex + 1),
                ],
              });
            }
          }
        }
      }

      Transforms.removeNodes(editor, {
        at: [...table[1], rowIndex],
      });
    });
  }

  static deleteColumn(editor: Editor, columnIndex: number) {
    const table = TableUtils.currentTable(editor);
    if (!table) return;

    const tableStructure = getTableStructureFromElement(editor, table[0]);
    if (!tableStructure) return;

    Editor.withoutNormalizing(editor, () => {
      for (let y = tableStructure.height - 1; y >= 0; y--) {
        const cell = tableStructure.cell(columnIndex, y);
        if (!cell) continue;

        const colSpan = cell.tableCell.table_colspan ?? 1;
        const coordsOfActual = tableStructure.pathToCoordinates(
          cell.relativePath
        );
        if (!coordsOfActual) continue;

        const atStartOfRowspan = coordsOfActual.rowIndex === y;

        if (atStartOfRowspan) {
          if (colSpan > 1) {
            // Decrease colspan by 1
            if (colSpan - 1 === 1) {
              Transforms.unsetNodes(editor, 'table_colspan', {
                at: [...table[1], ...cell.relativePath],
              });
            } else {
              Transforms.setNodes(
                editor,
                { table_colspan: colSpan - 1 },
                {
                  at: [...table[1], ...cell.relativePath],
                }
              );
            }
          } else {
            Transforms.removeNodes(editor, {
              at: [...table[1], ...cell.relativePath],
            });
          }
        }
      }

      TableUtils.removeColumnWidth(editor, table[1], columnIndex);
    });
  }

  static splitCells(editor: Editor, tablePath: Path, cells: CellInfo[]) {
    const table = Editor.node(editor, tablePath);
    if (!table || !isTableElement(table[0])) return;

    const tableStructure = getTableStructureFromElement(editor, table[0]);

    for (const cell of cells) {
      const rowSpan = Number(cell.tableCell.table_rowspan ?? 1);
      const colSpan = Number(cell.tableCell.table_colspan ?? 1);

      if (rowSpan === 1 && colSpan === 1) continue;

      Editor.withoutNormalizing(editor, () => {
        const coords = tableStructure.pathToCoordinates(cell.relativePath);
        if (!coords) return;

        const { rowIndex, columnIndex } = coords;

        for (let y = rowIndex; y < rowIndex + rowSpan; y++) {
          const actualColIndex = tableStructure.actualColumnIndex(
            columnIndex,
            y
          );

          for (let x = actualColIndex; x < actualColIndex + colSpan; x++) {
            if (x === actualColIndex && y === rowIndex) continue;

            // Insert a new table cell for each of the cells
            // covered by rowspan/colspan.
            Transforms.insertNodes(
              editor,
              { type: 'table-cell', children: [{ text: '' }] },
              { at: [...tablePath, y, x] }
            );
          }
        }

        // Remove the rowspan/colspan attributes from the origin cell.
        Transforms.unsetNodes(editor, ['table_rowspan', 'table_colspan'], {
          at: [...tablePath, ...cell.relativePath],
        });
      });
    }
  }

  // Split a cell into 2 at a specified index.
  // IMPORTANT: The index is relative to the table, not the cell.
  static splitCellAtIndex(
    editor: Editor,
    tablePath: Path,
    cellPosition: CellCoordinates,
    index: number,
    rowOrColumn: 'row' | 'column'
  ) {
    const table = Editor.node(editor, tablePath);
    if (!table || !isTableElement(table[0])) return;

    const tableStructure = getTableStructureFromElement(editor, table[0]);
    const cellInfo = tableStructure.cell(
      cellPosition.columnIndex,
      cellPosition.rowIndex
    );
    if (!cellInfo) return;

    const startPosition = tableStructure.pathToCoordinates(
      cellInfo.relativePath
    );
    if (!startPosition) return;

    const originalCellPath = [...table[1], ...cellInfo.relativePath];

    let startIndex = startPosition.columnIndex;
    let span = cellInfo.tableCell.table_colspan ?? 1;
    let property: keyof TableCellElement = 'table_colspan';
    let oppositeProperty: keyof TableCellElement = 'table_rowspan';
    let newCellPath = [
      ...originalCellPath.slice(0, -1),
      originalCellPath[originalCellPath.length - 1] + 1,
    ];

    if (rowOrColumn === 'row') {
      startIndex = startPosition.rowIndex;
      property = 'table_rowspan';
      oppositeProperty = 'table_colspan';
      span = cellInfo.tableCell.table_rowspan ?? 1;
      const actualColIndex = tableStructure.actualColumnIndex(
        startPosition.columnIndex,
        startPosition.rowIndex
      );
      newCellPath = [...table[1], index, actualColIndex];
    }

    if (span === 1) return;
    if (index <= startIndex) return;
    if (index >= startIndex + span) return;

    Editor.withoutNormalizing(editor, () => {
      // Insert a new table cell for each of the cells
      // covered by rowspan/colspan.
      Transforms.insertNodes(
        editor,
        { type: 'table-cell', children: [{ text: '' }] },
        { at: newCellPath }
      );

      // Update the span of the original cell, or unset the property if it's 1.
      const updatedSpan = index - startIndex;

      if (updatedSpan > 1) {
        Transforms.setNodes(
          editor,
          { [property]: updatedSpan },
          { at: originalCellPath }
        );
      } else {
        Transforms.unsetNodes(editor, property, {
          at: originalCellPath,
        });
      }

      // Update the span of the new cell, or unset the property if it's 1.
      const newSpan = span - updatedSpan;
      if (newSpan > 1) {
        Transforms.setNodes(
          editor,
          {
            [property]: newSpan,
            [oppositeProperty]: cellInfo.tableCell[oppositeProperty],
          },
          { at: newCellPath }
        );
      } else {
        Transforms.unsetNodes(editor, property, { at: newCellPath });
      }
    });
  }

  // Given a table and a cell position, get the range of the cell and split
  // the cells at the border of this range. Finally, merge to create a single
  // cell.
  static matchSizeOfCell(
    editor: Editor,
    table: NodeEntry<TableElement>,
    position: CellCoordinates,
    cell: TableCellElement
  ) {
    const tableStructure = getTableStructureFromElement(editor, table[0]);
    const rowspan = cell.table_rowspan ?? 1;
    const colspan = cell.table_colspan ?? 1;

    const range = new TableRange(position, {
      rowIndex: position.rowIndex + rowspan - 1,
      columnIndex: position.columnIndex + colspan - 1,
    });

    const { startPosition, endPosition } = range;

    for (let y = startPosition.rowIndex; y <= endPosition.rowIndex; y++) {
      for (
        let x = startPosition.columnIndex;
        x <= endPosition.columnIndex;
        x++
      ) {
        const cellInfo = tableStructure.cell(x, y);
        if (!cellInfo) continue;

        const actualCellPosition = tableStructure.pathToCoordinates(
          cellInfo.relativePath
        );
        if (!actualCellPosition) continue;

        const cellPosition = {
          rowIndex: Math.max(
            actualCellPosition.rowIndex,
            startPosition.rowIndex
          ),
          columnIndex: Math.max(
            actualCellPosition.columnIndex,
            startPosition.columnIndex
          ),
        };

        TableUtils.splitCellAtIndex(
          editor,
          table[1],
          cellPosition,
          startPosition.rowIndex,
          'row'
        );

        TableUtils.splitCellAtIndex(
          editor,
          table[1],
          cellPosition,
          endPosition.rowIndex + 1,
          'row'
        );

        TableUtils.splitCellAtIndex(
          editor,
          table[1],
          cellPosition,
          startPosition.columnIndex,
          'column'
        );

        TableUtils.splitCellAtIndex(
          editor,
          table[1],
          cellPosition,
          endPosition.columnIndex + 1,
          'column'
        );
      }
    }

    TableUtils.mergeCellsInRange(editor, table[1], range);
  }

  // Given a TableRange, merge all cells in the range into a single cell.
  static mergeCellsInRange(editor: Editor, tablePath: Path, range: TableRange) {
    const table = Editor.node(editor, tablePath) as NodeEntry<TableElement>;
    const tableStructure = getTableStructureFromElement(editor, table[0]);
    const { startPosition, endPosition } = range;
    const rangeColumns =
      endPosition.columnIndex - startPosition.columnIndex + 1;
    const rangeRows = endPosition.rowIndex - startPosition.rowIndex + 1;
    const cellPathsToMove: Path[] = [];

    const firstCell = tableStructure.cell(
      startPosition.columnIndex,
      startPosition.rowIndex
    );
    if (!firstCell) return;

    const firstCellPath = [...tablePath, ...firstCell.relativePath];
    const insertPosition = firstCell.tableCell.children.length;
    const cellsMemo = new Set<string>([firstCellPath.toString()]);

    for (let y = startPosition.rowIndex; y <= endPosition.rowIndex; y++) {
      for (
        let x = startPosition.columnIndex;
        x <= endPosition.columnIndex;
        x++
      ) {
        const cell = tableStructure.cell(x, y);
        if (!cell) continue;

        const cellPath = [...tablePath, ...cell.relativePath];
        if (!cellsMemo.has(cellPath.toString())) {
          cellPathsToMove.push(cellPath);
          cellsMemo.add(cellPath.toString());
        }
      }
    }

    Editor.withoutNormalizing(editor, () => {
      Transforms.unsetNodes(editor, ['table_rowspan', 'table_colspan'], {
        at: firstCellPath,
      });

      for (const cellPath of PathUtils.sortPathsByDepth(
        cellPathsToMove,
        true
      )) {
        if (!TableUtils.cellIsEmpty(editor, cellPath)) {
          const children = Array.from(
            Node.children(editor, cellPath, { reverse: true })
          );
          for (const child of children) {
            Transforms.insertNodes(editor, child[0], {
              at: [...tablePath, ...firstCell.relativePath, insertPosition],
            });
          }
        }

        Transforms.removeNodes(editor, { at: cellPath });
      }

      const props: Pick<TableCellElement, 'table_rowspan' | 'table_colspan'> =
        {};

      if (rangeRows > 1) props.table_rowspan = rangeRows;
      if (rangeColumns > 1) props.table_colspan = rangeColumns;

      if (Object.keys(props).length) {
        Transforms.setNodes(editor, props, {
          at: [...tablePath, ...firstCell.relativePath],
        });
      }
    });
  }

  static reloadCell(
    editor: Editor,
    tablePath: Path,
    cellCoords: CellCoordinates
  ) {
    const table = Editor.node(editor, tablePath) as NodeEntry<TableElement>;
    if (!table) return;

    const tableStructure = getTableStructureFromElement(editor, table[0]);
    return tableStructure.cell(cellCoords.columnIndex, cellCoords.rowIndex);
  }

  static cellIsEmpty(editor: Editor, cellPath: Path) {
    const cell = Editor.node(editor, cellPath) as NodeEntry<TableCellElement>;
    const cellChildren = cell[0].children;
    return (
      cellChildren.length === 1 &&
      isParagraphElement(cellChildren[0]) &&
      cellChildren[0].children.length === 1 &&
      Text.isText(cellChildren[0].children[0]) &&
      cellChildren[0].children[0].text === ''
    );
  }

  static clearCellContents(editor: Editor, path: Path) {
    const node = Node.get(editor, path);
    if (!isTableCellElement(node)) return;

    Editor.withoutNormalizing(editor, () => {
      for (let i = node.children.length - 1; i >= 0; i--) {
        Transforms.removeNodes(editor, {
          at: [...path, i],
        });
      }

      Transforms.insertNodes(editor, emptyParagraph(), {
        at: [...path, 0],
      });
    });
  }

  static clearSelectedCellContents(
    editor: Editor,
    tableSelection: TableSelection,
    tableStructure?: TableStructure
  ) {
    const tablePath = TableUtils.currentTablePath(editor);
    if (!tablePath) return;

    for (const relativePath of tableSelection.selectedCellPaths) {
      const path = [...tablePath, ...relativePath];

      TableUtils.clearCellContents(editor, path);
    }

    const range = tableSelection.tableRange;
    if (!range) return;

    const focusCell = range.focusPosition;
    const cell = tableStructure?.cell(
      focusCell.columnIndex,
      focusCell.rowIndex
    );
    if (!cell) return;

    Transforms.select(editor, [...tablePath, ...cell.relativePath]);
  }

  // Check if the selection is within a single table cell
  static isSelectionWithinSingleCell(editor: Editor) {
    if (!editor.selection) return false;

    const anchorCell = TableUtils.parentCell(
      editor,
      editor.selection.anchor.path
    );
    const focusCell = TableUtils.parentCell(
      editor,
      editor.selection.focus.path
    );
    if (!anchorCell || !focusCell) return false;

    // If we have an anchor cell and a focus cell and they have the same path
    // then the selection must be within a single cell
    return Path.equals(anchorCell[1], focusCell[1]);
  }

  // Check if all or part of a table is covered by the selection,
  // ignoring cases where the selection is within a single cell
  static isTableWithinSelection(editor: Editor) {
    // Check if any table nodes intersect with the selection
    const [match] = Editor.nodes(editor, {
      match: isTableElement,
    });
    if (!match) return false;

    // If the selection is within a single cell, we want to use normal
    // transforms and ignore the table selection
    return !TableUtils.isSelectionWithinSingleCell(editor);
  }

  // If a table is partially covered by the selection, expand it to cover
  // the entire table
  static expandRangeToTableEdges(editor: Editor, range: Range) {
    let { anchor, focus } = range;

    // We need to get the highest anchor and focus tables as the selection
    // could be in a cell of a table contained within another table.
    // If this is the case, the outer table will be be entirely selected.
    const anchorTable = TableUtils.parentTable(editor, anchor.path, 'highest');
    if (anchorTable) {
      anchor = Range.isBackward(range)
        ? Editor.end(editor, anchorTable[1])
        : Editor.start(editor, anchorTable[1]);
    }

    const focusTable = TableUtils.parentTable(editor, focus.path, 'highest');
    if (focusTable) {
      focus = Range.isBackward(range)
        ? Editor.start(editor, focusTable[1])
        : Editor.end(editor, focusTable[1]);
    }

    return { anchor, focus };
  }

  // Apply a callback to a selection that contains all or part of one or more
  // tables.
  static applyToTable(
    editor: Editor,
    tableSelection: TableSelection,
    callback: () => void,
    options: { hangRange?: boolean } = {}
  ) {
    if (!editor?.selection) return;

    // Save a selection ref so we can restore it after applying the callback
    const selectionRef = Editor.rangeRef(editor, editor.selection);

    if (tableSelection.multipleCellsSelected) {
      // If multiple cells are selected within a single table, apply the callback
      // to each of the selected cells.
      const tablePath = tableSelection.tablePath;
      if (!tablePath) return;

      const table = Editor.node(editor, tablePath);
      if (!table || !isTableElement(table[0])) return;

      const tableStructure = getTableStructureFromElement(editor, table[0]);
      if (!tableStructure) return;

      const cellPaths = Array.from(tableSelection.selectedCellPaths);

      for (const cell of cellPaths) {
        const cellStart = Editor.start(editor, [...table[1], ...cell]);
        let cellEnd = Editor.end(editor, [...table[1], ...cell]);

        if (options.hangRange) {
          const after = Editor.after(editor, cellEnd);
          if (after) cellEnd = after;
        }

        Transforms.select(editor, {
          anchor: cellStart,
          focus: cellEnd,
        });
        callback();
      }
    } else {
      // The selection may cover part of one or more tables so we need to expand
      // it to the edges of any tables that contain the anchor or focus.
      const { anchor, focus } = TableUtils.expandRangeToTableEdges(
        editor,
        editor.selection
      );

      Transforms.select(editor, { anchor, focus });

      callback();
    }

    const previousSelection = selectionRef.unref();
    if (previousSelection) {
      Transforms.select(editor, previousSelection);
    }
  }

  // Editor.marks() logic but for a table selection.
  // Returns the marks from the first text node.
  static tableSelectionMarks(
    editor: Editor,
    tableSelection: TableSelection
  ): Partial<Record<keyof Marks, unknown>> {
    if (!editor?.selection) return {};
    if (!TableUtils.isTableWithinSelection(editor)) return {};

    if (tableSelection?.multipleCellsSelected) {
      const tablePath = tableSelection.tablePath;
      if (!tablePath) return {};

      const table = Editor.node(editor, tablePath);
      if (!table || !isTableElement(table[0])) return {};

      const [path] = tableSelection.selectedCellPaths.values();
      if (path) {
        const [match] = Editor.nodes<Text>(editor, {
          at: {
            anchor: Editor.start(editor, [...table[1], ...path]),
            focus: Editor.end(editor, [...table[1], ...path]),
          },
          match: (n) => Text.isText(n),
        });
        if (match) {
          const { text, ...rest } = match[0];
          return rest;
        }
      }
    } else {
      // Expand the selection to cover complete tables.
      const { anchor, focus } = TableUtils.expandRangeToTableEdges(
        editor,
        editor.selection
      );

      const [match] = Editor.nodes(editor, {
        at: { anchor, focus },
        match: Text.isText,
      });

      if (match) {
        const { text, ...rest } = match[0];
        return rest;
      }
    }

    return {};
  }

  static tableSelectionContainsInline(
    editor: Editor,
    tableSelection: TableSelection,
    type: string
  ) {
    if (!editor?.selection) return false;
    if (!TableUtils.isTableWithinSelection(editor)) return false;

    if (tableSelection.multipleCellsSelected) {
      const tablePath = tableSelection.tablePath;
      if (!tablePath) return false;

      const table = Editor.node(editor, tablePath);
      if (!table || !isTableElement(table[0])) return false;

      const tableStructure = getTableStructureFromElement(editor, table[0]);
      if (!tableStructure) return false;

      const cellPaths = Array.from(tableSelection.selectedCellPaths);

      for (const cell of cellPaths) {
        const cellStart = Editor.start(editor, [...table[1], ...cell]);
        const cellEnd = Editor.end(editor, [...table[1], ...cell]);

        const [match] = Editor.nodes(editor, {
          at: { anchor: cellStart, focus: cellEnd },
          match: (n) =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === type,
        });

        if (match) return true;
      }
    } else {
      const { anchor, focus } = TableUtils.expandRangeToTableEdges(
        editor,
        editor.selection
      );

      const [match] = Editor.nodes(editor, {
        at: { anchor, focus },
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === type,
      });

      if (match) return true;
    }

    return false;
  }

  static fragmentIsOnlySingleCell(fragment: Descendant[]) {
    return (
      fragment.length === 1 &&
      isTableElement(fragment[0]) &&
      fragment[0].children.length === 1 &&
      isTableRowElement(fragment[0].children[0]) &&
      fragment[0].children[0].children.length === 1 &&
      isTableCellElement(fragment[0].children[0].children[0])
    );
  }

  static extractBlocksFromSingleCell(fragment: Descendant[]) {
    if (this.fragmentIsOnlySingleCell(fragment)) {
      // @ts-expect-error fragmentIsOnlySingleCell is a guard but not typed
      return fragment[0].children[0].children[0].children;
    }

    return fragment;
  }

  static canInsertOrDeleteRowsOrColumns(editor: Editor) {
    if (!editor.selection) return false;

    const table = TableUtils.currentTable(editor);
    if (!table) return false;

    const anchorCell = TableUtils.parentCell(
      editor,
      editor.selection.anchor.path
    );
    if (!anchorCell) return false;

    const focusCell = TableUtils.parentCell(
      editor,
      editor.selection.focus.path
    );
    if (!focusCell) return false;

    return Path.equals(anchorCell[1], focusCell[1]);
  }

  static removeTablesAtStartAndEndOfSelection(editor: Editor) {
    if (!editor.selection) return;

    const backward = Range.isBackward(editor.selection);
    const edges = Range.edges(editor.selection);

    const startPath = edges[0].path;
    const endPath = edges[1].path;

    const startTable = TableUtils.parentTable(editor, startPath);
    const endTable = TableUtils.parentTable(editor, endPath);

    if (startTable) {
      Transforms.removeNodes(editor, {
        match: isTableElement,
        at: startTable[1],
      });
      Transforms.insertNodes(editor, emptyParagraph(), {
        at: startTable[1],
      });
      Transforms.setSelection(editor, {
        [backward ? 'focus' : 'anchor']: Editor.point(editor, startTable[1]),
      });
    }

    if (endTable) {
      Transforms.removeNodes(editor, {
        match: isTableElement,
        at: endTable[1],
      });
      Transforms.insertNodes(editor, emptyParagraph(), {
        at: endTable[1],
      });
      Transforms.setSelection(editor, {
        [backward ? 'anchor' : 'focus']: Editor.point(editor, endTable[1]),
      });
    }
  }
}

export default TableUtils;
